import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';

const galleryImages = [
  {
    src: '/gallery/rhino-safari.jpg',
    alt: 'Rhino sighting on a Skiva Africa safari tour',
    caption: 'Wildlife Safari Experience',
    category: 'Safari',
  },
  {
    src: '/gallery/fnb-stadium-tour.jpg',
    alt: 'Skiva Africa tour group at FNB Stadium, Johannesburg',
    caption: 'FNB Stadium City Tour',
    category: 'City Tours',
  },
  {
    src: '/gallery/sandton-fleet.jpg',
    alt: 'Skiva Africa fleet vehicles lined up in Sandton',
    caption: 'Premium Fleet in Sandton',
    category: 'Fleet',
  },
  {
    src: '/gallery/staria-shuttle.jpg',
    alt: 'Hyundai Staria shuttle ready for guests',
    caption: 'Hyundai Staria Shuttle Service',
    category: 'Fleet',
  },
  {
    src: '/gallery/heineken-corporate.jpg',
    alt: 'Skiva Africa branded vehicles for Heineken Beverages corporate event',
    caption: 'Heineken Beverages Corporate',
    category: 'Corporate',
  },
  {
    src: '/gallery/Cultural Art Centers.jpg',
    alt: 'Cultural art center visit on a Skiva Africa tour',
    caption: 'Cultural Art Centers',
    category: 'Culture',
  },
  {
    src: '/gallery/Group Tours.png',
    alt: 'Group tour experience with Skiva Africa',
    caption: 'Group Tours',
    category: 'Group Tours',
  },
  {
    src: '/gallery/Mandela House.jpg',
    alt: 'Mandela House museum visit in Soweto',
    caption: 'Mandela House, Soweto',
    category: 'City Tours',
  },
  {
    src: '/gallery/Our Fleet.jpg',
    alt: 'Skiva Africa premium vehicle fleet showcase',
    caption: 'Our Premium Fleet',
    category: 'Fleet',
  },
  {
    src: '/gallery/Our Founder.jpg',
    alt: 'Founder of Skiva Africa Tours',
    caption: 'Our Founder',
    category: 'Team',
  },
  {
    src: '/gallery/Our Team.jpeg',
    alt: 'The Skiva Africa Tours team',
    caption: 'Our Team',
    category: 'Team',
  },
  {
    src: '/gallery/Safari Tours.jpg',
    alt: 'Safari tour through the African wilderness',
    caption: 'Safari Tours',
    category: 'Safari',
  },
  {
    src: '/gallery/Safari Tours.png',
    alt: 'Scenic safari landscape on a Skiva Africa tour',
    caption: 'Safari Adventures',
    category: 'Safari',
  },
  {
    src: '/gallery/Shuttle Services.jpg',
    alt: 'Skiva Africa shuttle service for guests',
    caption: 'Shuttle Services',
    category: 'Fleet',
  },
  {
    src: '/gallery/South African Culture.jpg',
    alt: 'Experiencing the vibrant South African culture',
    caption: 'South African Culture',
    category: 'Culture',
  },
  {
    src: '/gallery/South African Safari.png',
    alt: 'South African safari wildlife experience',
    caption: 'South African Safari',
    category: 'Safari',
  },
  {
    src: '/gallery/The Upside Down House.png',
    alt: 'The Upside Down House tourist attraction visit',
    caption: 'The Upside Down House',
    category: 'City Tours',
  },
  {
    src: '/gallery/Tours.jpeg',
    alt: 'Guests enjoying a Skiva Africa tour experience',
    caption: 'Tour Experiences',
    category: 'City Tours',
  },
];

const categories = ['All', ...Array.from(new Set(galleryImages.map((img) => img.category)))];

export function Gallery() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredImages = useMemo(
    () =>
      activeCategory === 'All'
        ? galleryImages
        : galleryImages.filter((img) => img.category === activeCategory),
    [activeCategory]
  );

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const goNext = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex + 1) % filteredImages.length);
    }
  };

  const goPrev = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex(
        (lightboxIndex - 1 + filteredImages.length) % filteredImages.length
      );
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') goNext();
    if (e.key === 'ArrowLeft') goPrev();
  };

  return (
    <section id="gallery" className="gallery-section">
      {/* Background decorative elements */}
      <div className="gallery-bg-glow" />

      <div className="container mx-auto px-4">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="gallery-header-line" />
            <Camera className="w-5 h-5 text-gold-base" />
            <div className="gallery-header-line" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-4">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light to-gold-base">Gallery</span>
          </h2>
          <p className="text-white/50 font-light max-w-xl mx-auto text-lg leading-relaxed">
            Moments captured across our tours, corporate events, and safari adventures throughout South Africa.
          </p>
        </motion.div>

        {/* Category filter tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="gallery-filters"
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setLightboxIndex(null); }}
              className={`gallery-filter-btn ${activeCategory === cat ? 'gallery-filter-btn--active' : ''}`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Gallery grid — adaptive masonry-style layout */}
        <motion.div
          layout
          className="gallery-grid"
        >
          <AnimatePresence mode="popLayout">
            {filteredImages.map((image, index) => (
              <motion.div
                key={image.src}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className={`gallery-item ${index === 0 && activeCategory === 'All' ? 'gallery-item--featured' : ''}`}
                onClick={() => openLightbox(index)}
              >
                <div className="gallery-item-inner">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="gallery-item-img"
                    loading="lazy"
                  />
                  {/* Overlay */}
                  <div className="gallery-item-overlay">
                    <span className="gallery-item-category">{image.category}</span>
                    <h3 className="gallery-item-caption">{image.caption}</h3>
                  </div>
                  {/* Corner accent */}
                  <div className="gallery-item-corner" />
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="gallery-lightbox"
            onClick={closeLightbox}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="dialog"
            aria-label="Image lightbox"
          >
            {/* Close button */}
            <button
              className="gallery-lightbox-close"
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Navigation */}
            <button
              className="gallery-lightbox-nav gallery-lightbox-nav--prev"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              aria-label="Previous image"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            <button
              className="gallery-lightbox-nav gallery-lightbox-nav--next"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              aria-label="Next image"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            {/* Image */}
            <motion.div
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.3 }}
              className="gallery-lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={filteredImages[lightboxIndex].src}
                alt={filteredImages[lightboxIndex].alt}
                className="gallery-lightbox-img"
              />
              <div className="gallery-lightbox-info">
                <span className="gallery-lightbox-category">
                  {filteredImages[lightboxIndex].category}
                </span>
                <p className="gallery-lightbox-caption">
                  {filteredImages[lightboxIndex].caption}
                </p>
                <span className="gallery-lightbox-counter">
                  {lightboxIndex + 1} / {filteredImages.length}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
